import type { AbstractIntlMessages } from "next-intl"
import type { Locale } from "./config"
import type { WindowType } from "@/components/desktop/types"

export const MESSAGE_BUNDLES = [
    "setup",
    "cartagePay",
    "settings",
    "explorer",
    "freightDashboard",
    "document",
    "tasks",
    "warehouseScheduling",
    "comms",
    "reports",
    "onboarding",
    "knowledgeFiles",
] as const
export type MessageBundle = (typeof MESSAGE_BUNDLES)[number]

type MessagesLoader = () => Promise<AbstractIntlMessages>

const BUNDLE_LOADERS: Record<Locale, Record<MessageBundle, MessagesLoader>> = {
    en: {
        setup: () => import("@/locales/en/setup.json").then(mod => mod.default),
        cartagePay: () =>
            import("@/locales/en/cartagePay.json").then(mod => mod.default),
        settings: () =>
            import("@/locales/en/settings.json").then(mod => mod.default),
        explorer: () =>
            import("@/locales/en/explorer.json").then(mod => mod.default),
        freightDashboard: () =>
            import("@/locales/en/freightDashboard.json").then(
                mod => mod.default
            ),
        document: () =>
            import("@/locales/en/document.json").then(mod => mod.default),
        tasks: () => import("@/locales/en/tasks.json").then(mod => mod.default),
        warehouseScheduling: () =>
            import("@/locales/en/warehouseScheduling.json").then(
                mod => mod.default
            ),
        comms: () => import("@/locales/en/comms.json").then(mod => mod.default),
        reports: () =>
            import("@/locales/en/reports.json").then(mod => mod.default),
        onboarding: () =>
            import("@/locales/en/onboarding.json").then(mod => mod.default),
        knowledgeFiles: () =>
            import("@/locales/en/knowledgeFiles.json").then(mod => mod.default),
    },
    es: {
        setup: () => import("@/locales/es/setup.json").then(mod => mod.default),
        cartagePay: () =>
            import("@/locales/es/cartagePay.json").then(mod => mod.default),
        settings: () =>
            import("@/locales/es/settings.json").then(mod => mod.default),
        explorer: () =>
            import("@/locales/es/explorer.json").then(mod => mod.default),
        freightDashboard: () =>
            import("@/locales/es/freightDashboard.json").then(
                mod => mod.default
            ),
        document: () =>
            import("@/locales/es/document.json").then(mod => mod.default),
        tasks: () => import("@/locales/es/tasks.json").then(mod => mod.default),
        warehouseScheduling: () =>
            import("@/locales/es/warehouseScheduling.json").then(
                mod => mod.default
            ),
        comms: () => import("@/locales/es/comms.json").then(mod => mod.default),
        reports: () =>
            import("@/locales/es/reports.json").then(mod => mod.default),
        onboarding: () =>
            import("@/locales/es/onboarding.json").then(mod => mod.default),
        knowledgeFiles: () =>
            import("@/locales/es/knowledgeFiles.json").then(mod => mod.default),
    },
}

const pendingLoads = new Map<string, Promise<AbstractIntlMessages>>()

export function loadMessageBundle({
    locale,
    bundle,
}: {
    locale: Locale
    bundle: MessageBundle
}): Promise<AbstractIntlMessages> {
    const key = `${locale}:${bundle}`
    const pending = pendingLoads.get(key)
    if (pending) return pending
    const load = BUNDLE_LOADERS[locale][bundle]().catch(error => {
        pendingLoads.delete(key)
        throw error
    })
    pendingLoads.set(key, load)
    return load
}

function isMessagesObject(value: unknown): value is AbstractIntlMessages {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function mergeMessages(
    base: AbstractIntlMessages,
    extra: AbstractIntlMessages
): AbstractIntlMessages {
    const merged: AbstractIntlMessages = { ...base }
    for (const [key, value] of Object.entries(extra)) {
        const existing = merged[key]
        merged[key] =
            isMessagesObject(existing) && isMessagesObject(value)
                ? mergeMessages(existing, value)
                : value
    }
    return merged
}

export const WINDOW_MESSAGE_BUNDLES = {
    wilson: [],
    comms: ["comms"],
    settings: ["settings", "document"],
    explorer: ["explorer", "settings", "document", "knowledgeFiles"],
    document: ["document", "explorer", "knowledgeFiles"],
    tasks: ["tasks", "document"],
    todos: ["tasks", "document"],
    setup: ["setup", "cartagePay"],
    onboarding: ["onboarding"],
    diagramViewer: [],
    reports: ["reports"],
    report: ["reports"],
    meetWilson: ["setup"],
    knowledgeNote: ["knowledgeFiles"],
    knowledgeDoc: ["knowledgeFiles"],
    template: ["knowledgeFiles"],
    warehouseScheduling: ["warehouseScheduling"],
    skill: ["explorer", "settings", "document"],
    freightDashboard: ["freightDashboard", "settings"],
    cartagePay: ["cartagePay", "setup"],
} as const satisfies Record<WindowType, readonly MessageBundle[]>

export const PUBLIC_FREIGHT_DASHBOARD_BUNDLES = [
    "freightDashboard",
] as const satisfies readonly MessageBundle[]

export const START_MESSAGE_BUNDLES = [
    "setup",
] as const satisfies readonly MessageBundle[]

export const WILSON_INSTRUCTIONS_BUNDLES = [
    "settings",
    "explorer",
    "document",
    "tasks",
] as const satisfies readonly MessageBundle[]
